import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  AppBar,
  Toolbar,
  Alert,
  Chip,
  Link,
} from "@mui/material";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimerIcon from "@mui/icons-material/Timer";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import GroupsIcon from "@mui/icons-material/Groups";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import { useDashboardWithPresentation } from "../hooks/useDashboardWithPresentation";
import { useDashboardTabState } from "../hooks/useDashboardTabState";
import {
  DashboardTabChrome,
  DashboardKpiSkeleton,
} from "./dashboard/DashboardTabChrome";
import DashboardChannelCompareSection from "./dashboard/DashboardChannelCompareSection";
import { useTheme, alpha, lighten, darken } from "@mui/material/styles";
import { useMutation } from "react-query";
import MetricAiInsightsDialog from "./MetricAiInsightsDialog";
import { getDashboardMetricInsights } from "../api/client";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";

/* ===== Highcharts (Sunburst) ===== */
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
// Import robusto do módulo Sunburst (funciona com ESM e CJS)
let Sunburst;
try {
  Sunburst = require("highcharts/modules/sunburst");
  if (Sunburst && typeof Sunburst === "function") {
    Sunburst(Highcharts);
  } else if (Sunburst && Sunburst.default && typeof Sunburst.default === "function") {
    Sunburst.default(Highcharts);
  }
} catch (err) {
  // Se algo quebrar no build, não derruba a tela
  console.error("Erro ao carregar módulo Highcharts Sunburst:", err);
}

/* ===== Utils ===== */
const BR = (x) => (Number.isFinite(x) ? x : Number(x ?? 0)).toLocaleString("pt-BR");

/** API envia `day` como YYYY-MM-DD; exibe no padrão brasileiro sem depender de fuso */
const formatChartDayBr = (day) => {
  if (day == null || day === "") return "";
  const m = String(day).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(day);
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const secToMMSS = (sec) => {
  const s = Math.max(0, Number(sec || 0));
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

const secToHuman = (sec) => {
  const s = Math.max(0, Math.round(Number(sec || 0)));
  if (!s) return "0m";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d${h ? ` ${h}h` : ""}`;
  if (h > 0) return `${h}h${m ? ` ${m}m` : ""}`;
  return `${m || 1}m`;
};

/* ===== Geração de paletas a partir do Tema ===== */
const buildSeriesFromTheme = (theme) => {
  const p = theme.palette;
  return [
    p.primary.main,
    (p.secondary?.main ?? p.info.main),
    p.warning.main,
    p.success.main,
    p.error.main,
    p.info.main,
    darken(p.primary.main, 0.2),
    lighten(p.primary.main, 0.35),
  ];
};

// Paleta raiz para famílias do Sunburst (TIPO)
const buildRootPalette = (theme) => {
  const p = theme.palette;
  return [
    p.primary.main,
    (p.secondary?.main ?? p.info.main),
    p.success.main,
    p.warning.main,
    p.error.main,
    p.info.main,
    (p.secondary?.dark ?? darken(p.info.main, 0.2)),
    p.success.dark,
    p.primary.dark,
    darken(p.text.secondary, 0.4),
  ];
};

/* ===== Mapeamento de STATUS -> cor do tema ===== */
/** Normaliza string para comparação (sem acentos, minúscula) */
const norm = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/** Deduz a colorKey do status a partir do nome que vem do backend. */
const statusKeyFromName = (nameOrCode) => {
  const s = norm(nameOrCode);
  // aceita também códigos numéricos como "0","1",... se vierem como name
  if (s === "0" || s.startsWith("pend")) return "pending";
  if (s === "1" || s.includes("atendimento") || s.includes("andamento") || s.includes("progres")) return "inProgress";
  if (s === "2" || s.startsWith("conclu")) return "success";
  if (s === "3" || s.includes("futuro") || s.includes("follow")) return "future";
  if (s === "4" || s.startsWith("perd") || s.includes("nao resol") || s.includes("não resol")) return "unsuccessful";
  if (s === "5" || s.startsWith("cancel")) return "canceled";
  return null;
};

/** Retorna a cor do tema para o status, ou null se não conseguir mapear. */
const colorFromStatusName = (theme, nameOrCode) => {
  const key = statusKeyFromName(nameOrCode);
  return key ? theme.palette?.status?.[key] ?? null : null;
};

function mapTimeQuality(raw, avgSecFallback) {
  const mode = String(raw?.mode || "");
  if (mode === "stored_estimate" || mode === "unavailable" || mode === "full") {
    return {
      mode,
      available: Boolean(raw.available),
      coveragePct: Number(raw.coveragePct || 0),
      sampleSize: raw.sampleSize == null ? null : Number(raw.sampleSize),
      eligibleSize: raw.eligibleSize == null ? null : Number(raw.eligibleSize),
      avgSec: raw.avgSec == null || raw.avgSec === "" ? null : Number(raw.avgSec),
    };
  }
  if (avgSecFallback == null || avgSecFallback === "") {
    return { mode: "full", available: false, coveragePct: 0, sampleSize: null, eligibleSize: null, avgSec: null };
  }
  return {
    mode: "full",
    available: true,
    coveragePct: 100,
    sampleSize: null,
    eligibleSize: null,
    avgSec: Number(avgSecFallback),
  };
}

/* ===== Mapas ===== */
function mapCharts(payload) {
  const safe = (x, def) => (x === undefined || x === null ? def : x);

  const totalAtendimentos = safe(payload?.cards?.totalAtendimentos, 0);
  const clientesComContatoHumano = safe(payload?.cards?.clientesComContatoHumano, 0);
  const tmaQuality = mapTimeQuality(payload?.cards?.tmaQuality, payload?.cards?.avgTMAsec);
  const tmeQuality = mapTimeQuality(payload?.cards?.tmeQuality, payload?.cards?.avgTMEsec);
  const avgTMAsec = tmaQuality.available ? tmaQuality.avgSec : null;
  const avgTMEsec = tmeQuality.available ? tmeQuality.avgSec : null;
  const deflectionPct = safe(payload?.cards?.botDeflectionPct, 0);

  const serieTemporalRaw = Array.isArray(payload?.charts?.serieTemporal) ? payload.charts.serieTemporal : [];
  const emProcessamentoRaw = Array.isArray(payload?.charts?.emProcessamento) ? payload.charts.emProcessamento : [];
  const finalizadosRaw = Array.isArray(payload?.charts?.finalizados) ? payload.charts.finalizados : [];
  const motivosRaw = Array.isArray(payload?.charts?.motivos) ? payload.charts.motivos : [];
  const motivosTopRaw = Array.isArray(payload?.charts?.motivosTop) ? payload.charts.motivosTop : [];

  return {
    cards: {
      totalAtendimentos,
      clientesComContatoHumano,
      avgTMAsec,
      avgTMEsec,
      tmaFmt: avgTMAsec == null ? "—" : secToHuman(avgTMAsec),
      tmeFmt: avgTMEsec == null ? "—" : secToHuman(avgTMEsec),
      tmaQuality,
      tmeQuality,
      peopleContactedMode: payload?.cards?.peopleContactedMode || "messages_join",
      deflectionPct: String(deflectionPct),
    },
    charts: {
      serieTemporal: serieTemporalRaw.map((r) => ({ day: r.day, value: Number(r.value || 0) })),
      emProcessamento: emProcessamentoRaw.map((d) => ({ name: d.name, value: Number(d.y || 0) })),
      finalizados: finalizadosRaw.map((d) => ({ name: d.name, value: Number(d.y || 0) })),
      // Motivos já vem no formato {id,parent,name,value}
      motivos: motivosRaw.map((m) => ({
        id: String(m.id),
        parent: m.parent ? String(m.parent) : "",
        name: m.name,
        value: Number(m.value || 0),
      })),
      motivosTop: motivosTopRaw.map((r) => ({ path: r.path, value: Number(r.value || 0) })),
      channelCompare: mapChannelCompare(payload?.charts?.channelCompare),
      handlerCompare: mapHandlerCompare(payload?.charts?.handlerCompare),
    },
  };
}

function mapChannelCompare(raw) {
  if (!raw || typeof raw !== "object") return null;
  const rows = Array.isArray(raw?.rows) ? raw.rows : [];
  return {
    rows: rows.map((row) => ({
      name: String(row?.name || ""),
      isWhatsapp: Boolean(row?.isWhatsapp),
      isOthers: Boolean(row?.isOthers) || row?.name === "__others__",
      whatsapp: Number(row?.whatsapp || 0),
      webchat: Number(row?.webchat || 0),
      other: Number(row?.other || 0),
      volume: Number(row?.volume || 0),
      salesOrders: Number(row?.salesOrders || 0),
      salesRevenue: Number(row?.salesRevenue || 0),
      salesWhatsapp: Number(row?.salesWhatsapp || 0),
      salesWebchat: Number(row?.salesWebchat || 0),
      salesOther: Number(row?.salesOther || 0),
      ordersWhatsapp: Number(row?.ordersWhatsapp || 0),
      ordersWebchat: Number(row?.ordersWebchat || 0),
      ordersOther: Number(row?.ordersOther || 0),
    })),
    grouped: Boolean(raw?.grouped),
    totalCount: Number(raw?.totalCount || rows.length),
    hasWebchat: Boolean(raw?.hasWebchat),
    hasSales: Boolean(raw?.hasSales),
  };
}

function mapHandlerCompare(raw) {
  if (!raw || typeof raw !== "object") return null;
  const rows = Array.isArray(raw?.rows) ? raw.rows : [];
  return {
    rows: rows.map((row) => ({
      key: String(row?.key || ""),
      volume: Number(row?.volume || 0),
      salesOrders: Number(row?.salesOrders || 0),
      salesRevenue: Number(row?.salesRevenue || 0),
    })),
    hasSales: Boolean(raw?.hasSales),
  };
}

function mapOperadores(payload) {
  let rowsRaw = [];
  if (Array.isArray(payload)) rowsRaw = payload;
  else if (Array.isArray(payload?.data)) rowsRaw = payload.data;
  else if (Array.isArray(payload?.rows)) rowsRaw = payload.rows;
  else if (Array.isArray(payload?.data?.data)) rowsRaw = payload.data.data;

  return {
    rows: rowsRaw.map((r, i) => ({
      id: r.idUsuario ?? r.id ?? i + 1,
      operador: r.operador ?? "-",
      ultimoAcesso: r.ultimoAcesso ?? "-",
      avgTMAsec: Math.round(Number(r.avgTMAsec ?? r.avg_tma ?? 0)) || 0,
      avgTMEsec: Math.round(Number(r.avgTMEsec ?? r.avg_tme ?? 0)) || 0,
      tma: secToHuman(r.avgTMAsec ?? r.avg_tma),
      tme: secToHuman(r.avgTMEsec ?? r.avg_tme),
      total: Number(r.total ?? 0),
      pendentes: Number(r.pendentes ?? 0),
      emAtendimento: Number(r.emAtendimento ?? 0),
      contatoFuturo: Number(r.contatoFuturo ?? 0),
      concluidas: Number(r.concluidas ?? 0),
      perdidas: Number(r.perdidas ?? 0),
      canceladas: Number(r.canceladas ?? 0),
      pessoasContatadas: Number(r.pessoasContatadas ?? r.pessoas_contatadas ?? 0),
    })),
  };
}

/* ===== UI auxiliares ===== */
const KpiCard = ({ icon, title, value, color, sub }) => (
  <Card>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {title}
            </Typography>
          </Stack>
          <Typography variant="h4" sx={{ color, lineHeight: 1.1 }}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

function TimeMetricCaption({ quality, fullHint, t, onNarrowPeriod }) {
  const mode = quality?.mode || "full";
  if (mode === "stored_estimate") {
    return (
      <Stack spacing={0.25}>
        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
          <Tooltip
            title={t("analytics.timeMetricEstimateTooltip", {
              pct: quality.coveragePct,
              defaultValue:
                "Média das solicitações que já tinham tempo gravado ({{pct}}% da amostra). Período grande demais para recalcular tudo.",
            })}
          >
            <Chip
              size="small"
              label={t("analytics.timeMetricEstimateChip", { defaultValue: "Estimativa" })}
              color="warning"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t("analytics.timeMetricEstimateHint", {
            pct: quality.coveragePct,
            defaultValue: "Com base em {{pct}}% das solicitações com tempo registrado",
          })}
        </Typography>
      </Stack>
    );
  }
  if (mode === "unavailable") {
    return (
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          {t("analytics.timeMetricUnavailableHint", {
            defaultValue: "Indisponível neste volume — o cálculo completo sobrecarregaria o banco.",
          })}
        </Typography>
        {typeof onNarrowPeriod === "function" ? (
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            onClick={onNarrowPeriod}
            sx={{ textAlign: "left", cursor: "pointer" }}
          >
            {t("analytics.timeMetricNarrowCta", { defaultValue: "Ver últimos 7 dias" })}
          </Link>
        ) : null}
      </Stack>
    );
  }
  return (
    <Typography variant="caption" color="text.secondary">
      {fullHint}
    </Typography>
  );
}

const CustomPieTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <Box sx={{ backgroundColor: "#fff", p: 1.2, borderRadius: 1, boxShadow: "0 0 10px rgba(0,0,0,.2)" }}>
      <Typography variant="subtitle2" fontWeight="bold">{p.name}</Typography>
      <Typography variant="body2">Quantidade: {BR(p.value)}</Typography>
    </Box>
  );
};

/** Altura do sunburst inline no dashboard (Top caminhos usa a mesma faixa com scroll). */
const ATENDIMENTO_SUNBURST_INLINE_HEIGHT = 480;

/* ===== Sunburst (Highcharts) – builder de opções ===== */
const buildSunburstOptions = ({
  data = [],
  total = 0,
  rootPalette = [],
  tooltipBg = "#fff",
  tooltipText = "#111",
  fontFamily = "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial",
  overviewLabel = "Visão geral",
  requestsLabel = "solicitações",
  chartHeight = ATENDIMENTO_SUNBURST_INLINE_HEIGHT,
  innerRingPixels = 34,
  overviewColor = "#6b7280",
  totalColor = "#111827",
  centerLabelBg = "rgba(255,255,255,0.94)",
  centerLabelShadow = "0 1px 8px rgba(0,0,0,0.08)",
  /** Caixa menor no modo dashboard para não invadir o primeiro anel do sunburst */
  centerCompact = true,
  /** Largura fixa (px) — tela cheia preenche o container */
  chartWidth,
  presentation = "inline",
}) => {
  const isFs = presentation === "fullscreen";
  const cPad = centerCompact ? "4px 7px" : "10px 16px";
  const cRadius = centerCompact ? "10px" : "14px";
  const cMaxW = centerCompact ? (isFs ? "92px" : "100px") : "168px";
  const fsOv = centerCompact ? (isFs ? "9px" : "9px") : "12px";
  const fsTot = centerCompact ? (isFs ? "12px" : "12px") : "18px";
  const fsReq = centerCompact ? (isFs ? "8px" : "8px") : "11px";
  const cShadow = centerCompact ? "0 1px 4px rgba(0,0,0,0.07)" : centerLabelShadow;
  /* Fullscreen: fonte um pouco maior, mas sem allowOverlap agressivo (evita “explosão” de rótulos) */
  const dlBase = isFs ? "12px" : "11px";
  const dl1 = isFs ? "13px" : "12px";
  const dl2 = isFs ? "12px" : "11px";
  const dl34 = isFs ? "11px" : "10px";
  const arcFilterMin = isFs ? 7 : 8;
  let cBg = centerLabelBg;
  if (centerCompact) {
    try {
      cBg = Highcharts.color(centerLabelBg).setOpacity(0.76).get("rgba");
    } catch {
      cBg = centerLabelBg;
    }
  }
  // Mapa id -> nó original (para reconstruir o caminho no tooltip)
  const byId = {};
  data.forEach((d) => { if (d && d.id != null) byId[String(d.id)] = d; });

  // --- helpers para cor/nível ---
  const getDepth = (node) => {
    let depth = 0;
    let cur = node;
    const guard = new Set();
    while (cur && cur.parent && !guard.has(cur.id)) {
      guard.add(cur.id);
      depth++;
      cur = byId[String(cur.parent)];
    }
    return depth; // 0 = Tipo, 1 = Sub1, 2 = Sub2, 3 = Sub3
  };

  // Variações (brightness) por nível
  const BRIGHT_BY_LEVEL = [0, -0.15, -0.28, -0.38];

  // Tipos (raiz) para colorir famílias
  const level1Ids = data.filter((d) => !d.parent).map((d) => d.id);
  const colorMap = new Map();
  level1Ids.forEach((id, i) =>
    colorMap.set(id, rootPalette[i % rootPalette.length] || "#1976d2")
  );

  // Aplica cor-base por família + variação por nível
  const coloredData = data.map((n) => {
    // acha a raiz da família (tipo)
    let rootId = n.id;
    let cur = n;
    const guard = new Set();
    while (cur && cur.parent && !guard.has(cur.id)) {
      guard.add(cur.id);
      rootId = cur.parent;
      cur = byId[String(cur.parent)];
    }
    const base = colorMap.get(rootId) || "#1976d2";
    const depth = getDepth(n);
    const bright = BRIGHT_BY_LEVEL[Math.min(depth, BRIGHT_BY_LEVEL.length - 1)];
    const color = Highcharts.color(base).brighten(bright).get();
    return { ...n, color };
  });

  // Helper: reconstrói "Tipo › Sub1 › Sub2 › Sub3"
  const buildPath = (pt) => {
    const segs = [];
    let cur = byId[String(pt.id)];
    let guard = 0;
    while (cur && guard++ < 50) {
      segs.unshift(cur.name || "");
      if (!cur.parent) break;
      cur = byId[String(cur.parent)];
    }
    return segs.filter(Boolean).join(" › ");
  };

  const chartCfg = {
    height: chartHeight,
    spacingTop: isFs ? 6 : 10,
    spacingBottom: isFs ? 12 : 14,
    spacingLeft: isFs ? 4 : 6,
    spacingRight: isFs ? 4 : 6,
    style: { fontFamily },
    backgroundColor: "transparent",
  };
  if (chartWidth != null && chartWidth > 0) {
    chartCfg.width = chartWidth;
  }

  return {
    chart: chartCfg,
    title: { text: null },
    credits: { enabled: false },
    tooltip: {
      useHTML: true,
      backgroundColor: tooltipBg,
      style: { color: tooltipText },
      borderRadius: 8,
      formatter: function () {
        const path = buildPath(this.point);
        const pct =
          (this.point.node && typeof this.point.node.percentage === "number")
            ? this.point.node.percentage
            : (typeof this.point.percentage === "number" ? this.point.percentage : 0);
        return `<b>${path}</b><br/>${BR(this.point.value)} solic. &nbsp;•&nbsp; ${pct.toFixed(1)}%`;
      },
    },
    legend: { enabled: false },
    plotOptions: { series: { animation: { duration: 200 } } },
    series: [
      {
        type: "sunburst",
        data: coloredData,
        borderRadius: 2,
        allowTraversingTree: true,
        cursor: "pointer",
        dataLabels: {
          rotationMode: "circular",
          allowOverlap: !isFs,
          padding: 2,
          ...(isFs ? { crop: true } : { crop: false, overflow: "allow" }),
          style: {
            textOutline: "none",
            fontSize: dlBase,
            fontWeight: "500",
            textOverflow: "clip",
          },
          filter: { property: "innerArcLength", operator: ">", value: arcFilterMin },
          format: "{point.name}",
        },
        levels: [
          {
            level: 1,
            levelIsConstant: false,
            borderWidth: 1,
            levelSize: { unit: "pixels", value: innerRingPixels },
            dataLabels: {
              rotationMode: "perpendicular",
              style: { fontWeight: 600, fontSize: dl1, textOverflow: "clip" },
            },
          },
          {
            level: 2,
            borderWidth: 1,
            dataLabels: { style: { fontSize: dl2, textOverflow: "clip" } },
          },
          {
            level: 3,
            borderWidth: 1,
            dataLabels: { style: { fontSize: dl34, textOverflow: "clip" } },
          },
          {
            level: 4,
            borderWidth: 1,
            dataLabels: { style: { fontSize: dl34, textOverflow: "clip" } },
          },
        ],
        point: {
          events: {
            click: function () {
              if (this.series && typeof this.series.setRootNode === "function") {
                this.series.setRootNode(this.id);
              }
            },
          },
        },
      },
    ],
    breadcrumbs: {
      floating: true,
      position: { align: "left" },
      showFullPath: true,
      buttonTheme: {
        fill: "transparent",
        padding: 4,
        style: { fontWeight: 500 },
        states: { hover: { fill: "rgba(0,0,0,0.06)" } },
      },
    },
    subtitle: {
      useHTML: true,
      verticalAlign: "middle",
      floating: true,
      text: `<div style="text-align:center;pointer-events:none;">
               <div style="display:inline-block;text-align:center;box-sizing:border-box;max-width:${cMaxW};padding:${cPad};border-radius:${cRadius};background:${cBg};box-shadow:${cShadow}">
                 <div style="font-size:${fsOv};color:${overviewColor};line-height:1.15">${overviewLabel}</div>
                 <div style="font-size:${fsTot};font-weight:700;color:${totalColor};line-height:1.15;margin-top:1px">${BR(total)}</div>
                 <div style="font-size:${fsReq};color:${overviewColor};line-height:1.1;margin-top:1px;opacity:0.92">${requestsLabel}</div>
               </div>
             </div>`,
    },
  };
};

/* ======================= COMPONENTE ======================= */
const Atendimento = ({
  start,
  end,
  ids_origens = [],
  ids_tipos = [],
  ids_subtipo1 = [],
  ids_subtipo2 = [],
  ids_subtipo3 = [],
  ids_status = [],
  ids_usuarios = [],
  ids_agentes = [],
  ids_usuarios_interacao = [],
  ids_agentes_interacao = [],
  tags = [],
  tipo_responsavel = "",
  date_field = "entrada",
  all_period = false,
  ids_produtos = [],
  ids_campaigns = [],
  ids_whatsapp_campaigns = [],
  ids_motivos = [],
  ids_potenciais = [],
  ops_preset = "",
  stale_days,
  search = "",
  tipo_scope = "",
  presentationSnapshot = null,
  readOnly = false,
  onNarrowPeriod,
}) => {
  const { t } = useTranslation("chat");
  const { t: tSettings } = useTranslation("settings");
  const previewPendingMessage = tSettings("dashboardPage.shared.previewSectionPending");
  const theme = useTheme();

  React.useEffect(() => {
    ensureNamespaceLoaded("chat");
    ensureNamespaceLoaded("settings");
  }, []);

  // Paletas derivadas do tema (para KPIs, Recharts e Sunburst)
  const SERIES = React.useMemo(() => buildSeriesFromTheme(theme), [theme]);
  const ROOT_PALETTE = React.useMemo(() => buildRootPalette(theme), [theme]);

  /* --------- Queries (summary = 1 request, 1× refresh TMA/TME no backend) --------- */
  const atendimentoQ = useDashboardWithPresentation(
    "atendimento",
    "summary",
    {
      ...(all_period
        ? { all_period: 1 }
        : { start, end }),
      ids_origens,
      ids_tipos,
      ids_subtipo1,
      ids_subtipo2,
      ids_subtipo3,
      ids_status,
      ids_usuarios,
      ids_agentes,
      ids_usuarios_interacao,
      ids_agentes_interacao,
      tags,
      tipo_responsavel,
      date_field: date_field === "atualizacao" ? "atualizacao" : undefined,
      ...(Array.isArray(ids_produtos) && ids_produtos.length ? { ids_produtos } : {}),
      ...(Array.isArray(ids_campaigns) && ids_campaigns.length ? { ids_campaigns } : {}),
      ...(Array.isArray(ids_whatsapp_campaigns) && ids_whatsapp_campaigns.length
        ? { ids_whatsapp_campaigns }
        : {}),
      ...(Array.isArray(ids_motivos) && ids_motivos.length ? { ids_motivos } : {}),
      ...(Array.isArray(ids_potenciais) && ids_potenciais.length ? { ids_potenciais } : {}),
      ...(ops_preset ? { ops_preset } : {}),
      ...(ops_preset === "stale" && stale_days ? { stale_days } : {}),
      // search textual não é exposto no UI do dashboard; mantido só se caller legado passar.
      ...(search ? { search } : {}),
      ...(tipo_scope ? { tipo_scope } : {}),
    },
    {
      select: (payload) => ({
        charts: mapCharts(payload),
        operadores: mapOperadores(payload?.operadores || {}),
      }),
      staleTime: 900_000,
      enabled: true,
    },
    presentationSnapshot
  );

  const tab = useDashboardTabState(atendimentoQ, {
    registerShare: !readOnly && !presentationSnapshot,
  });
  const loading = tab.showSkeletons;
  const isPreview = tab.isPreview;

  /* --------- Derivações --------- */
  const chartsData = tab.data?.charts;
  const totalAtendimentos = chartsData?.cards?.totalAtendimentos ?? 0;
  const clientesComContatoHumano = chartsData?.cards?.clientesComContatoHumano ?? 0;
  const tmaFmt = chartsData?.cards?.tmaFmt ?? "—";
  const tmeFmt = chartsData?.cards?.tmeFmt ?? "—";
  const avgTMAsec = chartsData?.cards?.avgTMAsec;
  const avgTMEsec = chartsData?.cards?.avgTMEsec;
  const tmaQuality = chartsData?.cards?.tmaQuality;
  const tmeQuality = chartsData?.cards?.tmeQuality;
  const peopleContactedMode = chartsData?.cards?.peopleContactedMode || "messages_join";
  const serieTemporal   = chartsData?.charts?.serieTemporal   ?? [];
  const emProcessamento = chartsData?.charts?.emProcessamento ?? [];
  const finalizados     = chartsData?.charts?.finalizados     ?? [];
  const motivosData     = chartsData?.charts?.motivos         ?? [];
  const motivosTop      = chartsData?.charts?.motivosTop      ?? [];
  const channelCompare  = chartsData?.charts?.channelCompare  ?? null;
  const handlerCompare  = chartsData?.charts?.handlerCompare  ?? null;

  const rowsOperadores = tab.data?.operadores?.rows ?? [];

  const fmtDate = (iso) => {
    if (!iso || iso === "-") return "-";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return String(iso);
      return d.toLocaleString("pt-BR");
    } catch {
      return String(iso);
    }
  };

  const numSort = (v1, v2) => (Number(v1 ?? 0) - Number(v2 ?? 0));

  const secRowSort = (aRow, bRow, key) => (Number(aRow?.[key] ?? 0) - Number(bRow?.[key] ?? 0));

  const columnsOperadores = React.useMemo(
    () => [
      { field: "operador", headerName: "Operador", flex: 1, minWidth: 220, renderCell: (p) => p.row?.operador ?? "-" },
      { field: "ultimoAcesso", headerName: "Último acesso", width: 190, renderCell: (p) => fmtDate(p.row?.ultimoAcesso) },
      {
        field: "tma",
        headerName: "TMA",
        width: 120,
        minWidth: 100,
        align: "left",
        headerAlign: "left",
        sortComparator: (v1, v2, p1, p2) => secRowSort(p1.row, p2.row, "avgTMAsec"),
        renderCell: (p) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 700, color: SERIES[1], lineHeight: 1.35, m: 0 }}
            >
              {p.row?.tma ?? "0m"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "tme",
        headerName: "TME",
        width: 120,
        minWidth: 100,
        align: "left",
        headerAlign: "left",
        sortComparator: (v1, v2, p1, p2) => secRowSort(p1.row, p2.row, "avgTMEsec"),
        renderCell: (p) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 700, color: SERIES[2], lineHeight: 1.35, m: 0 }}
            >
              {p.row?.tme ?? "0m"}
            </Typography>
          </Box>
        ),
      },
      { field: "total", headerName: "Total", width: 100, type: "number", renderCell: (p) => BR(p.row?.total), sortComparator: numSort },
      {
        field: "pessoasContatadas",
        headerName: t("analytics.peopleContactedColumn"),
        width: 130,
        minWidth: 110,
        type: "number",
        renderCell: (p) => BR(p.row?.pessoasContatadas),
        sortComparator: numSort,
      },
      { field: "pendentes", headerName: "Pendentes", width: 120, type: "number", renderCell: (p) => BR(p.row?.pendentes), sortComparator: numSort },
      { field: "emAtendimento", headerName: "Em atendimento", width: 150, type: "number", renderCell: (p) => BR(p.row?.emAtendimento), sortComparator: numSort },
      { field: "contatoFuturo", headerName: "Contato Futuro", width: 150, type: "number", renderCell: (p) => BR(p.row?.contatoFuturo), sortComparator: numSort },
      { field: "concluidas", headerName: "Concluídas", width: 130, type: "number", renderCell: (p) => BR(p.row?.concluidas), sortComparator: numSort },
      { field: "perdidas", headerName: "Perdidas", width: 120, type: "number", renderCell: (p) => BR(p.row?.perdidas), sortComparator: numSort },
      { field: "canceladas", headerName: "Canceladas", width: 130, type: "number", renderCell: (p) => BR(p.row?.canceladas), sortComparator: numSort },
    ],
    [SERIES, t]
  );

  const [sunburstFsOpen, setSunburstFsOpen] = React.useState(false);
  const [sunburstFsSize, setSunburstFsSize] = React.useState({ w: 0, h: 0 });
  const sunburstFsBoxRef = React.useRef(null);
  const sunburstFsChartRef = React.useRef(null);

  const openSunburstFullscreen = React.useCallback(() => {
    if (typeof window !== "undefined") {
      const toolbarH = 48;
      setSunburstFsSize({
        w: window.innerWidth,
        h: Math.max(320, window.innerHeight - toolbarH),
      });
    }
    setSunburstFsOpen(true);
  }, []);

  React.useLayoutEffect(() => {
    if (!sunburstFsOpen) return undefined;
    const el = sunburstFsBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr || cr.width < 8 || cr.height < 8) return;
      const w = Math.floor(cr.width);
      const h = Math.floor(cr.height);
      setSunburstFsSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [sunburstFsOpen]);

  /** Sunburst é radial: largura ≠ altura quebra o desenho; usar quadrado = min(w,h). */
  const sunburstFullscreenChartSide = React.useMemo(() => {
    let w = sunburstFsSize.w;
    let h = sunburstFsSize.h;
    if (typeof window !== "undefined") {
      if (w < 1) w = window.innerWidth;
      if (h < 1) h = Math.max(320, window.innerHeight - 48);
    } else {
      if (w < 1) w = 960;
      if (h < 1) h = 640;
    }
    const side = Math.floor(Math.min(w, h) * 0.9);
    return Math.max(300, Math.min(side, 1600));
  }, [sunburstFsSize.w, sunburstFsSize.h]);

  React.useLayoutEffect(() => {
    if (!sunburstFsOpen) return;
    const chart = sunburstFsChartRef.current;
    if (chart && typeof chart.reflow === "function") {
      requestAnimationFrame(() => chart.reflow());
    }
  }, [sunburstFsOpen, sunburstFullscreenChartSide]);

  const sunburstThemeProps = React.useMemo(
    () => ({
      tooltipBg: theme.palette.mode === "dark" ? alpha("#000", 0.85) : alpha("#fff", 0.96),
      tooltipText: theme.palette.text.primary,
      fontFamily: theme.typography?.fontFamily,
      overviewColor:
        typeof theme.palette.text.secondary === "string"
          ? theme.palette.text.secondary
          : "#6b7280",
      totalColor:
        typeof theme.palette.text.primary === "string" ? theme.palette.text.primary : "#111827",
      centerLabelBg:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.background.paper, 0.96)
          : "rgba(255,255,255,0.94)",
      centerLabelShadow:
        theme.palette.mode === "dark"
          ? `0 2px 16px ${alpha("#000", 0.45)}`
          : "0 1px 8px rgba(0,0,0,0.08)",
    }),
    [theme]
  );

  /* Sunburst options (inline e tela cheia) */
  const sunburstOptions = React.useMemo(
    () =>
      buildSunburstOptions({
        data: motivosData,
        total: totalAtendimentos,
        rootPalette: ROOT_PALETTE,
        ...sunburstThemeProps,
        overviewLabel: t("analytics.overviewLabel"),
        requestsLabel: t("analytics.requestsLabel"),
        chartHeight: ATENDIMENTO_SUNBURST_INLINE_HEIGHT,
        innerRingPixels: 36,
        presentation: "inline",
      }),
    [motivosData, totalAtendimentos, ROOT_PALETTE, sunburstThemeProps, t]
  );

  const sunburstOptionsFullscreen = React.useMemo(
    () =>
      buildSunburstOptions({
        data: motivosData,
        total: totalAtendimentos,
        rootPalette: ROOT_PALETTE,
        ...sunburstThemeProps,
        overviewLabel: t("analytics.overviewLabel"),
        requestsLabel: t("analytics.requestsLabel"),
        chartHeight: sunburstFullscreenChartSide,
        chartWidth: sunburstFullscreenChartSide,
        innerRingPixels: 34,
        centerCompact: true,
        presentation: "fullscreen",
      }),
    [
      motivosData,
      totalAtendimentos,
      ROOT_PALETTE,
      sunburstThemeProps,
      t,
      sunburstFullscreenChartSide,
    ]
  );

  /* ===== Helpers para cores dos Pies por STATUS ===== */
  const statusCellColor = (slice, index) => {
    // tenta pegar a cor do status pelo nome; se não conseguir, cai para paleta SERIES
    return colorFromStatusName(theme, slice?.name) || SERIES[index % SERIES.length];
  };

  const [metricDialogOpen, setMetricDialogOpen] = React.useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = React.useState(null); // "tma" | "tme"
  const [metricDialogName, setMetricDialogName] = React.useState("");
  const [metricDialogLabel, setMetricDialogLabel] = React.useState("");
  const [metricDialogData, setMetricDialogData] = React.useState(null);
  const [metricDialogError, setMetricDialogError] = React.useState(null);
  const [metricDialogHuman, setMetricDialogHuman] = React.useState("");
  const [metricDialogBasePayload, setMetricDialogBasePayload] = React.useState(null);
  const lastCfgRef = React.useRef(null);

  const metricInsightsMutation = useMutation(
    async (cfg) => {
      const payload = {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricDefinition: cfg.metricDefinition,
        metricValue: cfg.metricValue,
        metricSec: cfg.metricSec,
        metricHuman: cfg.metricHuman,
        period: { start, end },
        summaryContext: {
          totalAtendimentos,
          clientesComContatoHumano,
          topMotivos: Array.isArray(motivosTop) ? motivosTop.slice(0, 10) : [],
        },
        filters: {
          ids_origens,
          ids_tipos,
          ids_subtipo1,
          ids_subtipo2,
          ids_subtipo3,
          ids_status,
          search,
          ...(tipo_scope ? { tipo_scope } : {}),
        },
      };
      return getDashboardMetricInsights("atendimento", payload);
    },
    {
      onSuccess: (data) => {
        setMetricDialogData(data || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          "Não foi possível gerar a explicação da IA no momento.";
        setMetricDialogError(msg);
      },
    }
  );

  const openAi = (cfg) => {
    setMetricDialogMetric(cfg.metric);
    setMetricDialogName(cfg.metricName || "");
    setMetricDialogLabel(cfg.metricLabel || "");
    setMetricDialogHuman(cfg.metricHuman || "");
    setMetricDialogBasePayload(cfg);
    setMetricDialogOpen(true);
    setMetricDialogData(null);
    setMetricDialogError(null);
    lastCfgRef.current = cfg;
    metricInsightsMutation.mutate(cfg);
  };

  const AiBtn = ({ cfg }) => {
    if (readOnly) return null;
    return (
      <Tooltip title={t("analytics.explainAi")}>
        <IconButton size="small" onClick={() => openAi(cfg)} aria-label={t("analytics.explainAiAria")}>
          <AutoAwesomeIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box>
      <DashboardTabChrome
        isDeferred={tab.isDeferred}
        isPreview={tab.isPreview}
        isInitialLoading={tab.showSkeletons}
        errorMessage={tab.errorMessage}
        previewPendingMessage={previewPendingMessage}
      />
      {String(tipo_scope || "").trim().toLowerCase() === "relacionamento" ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 860 }}>
          {tSettings("dashboardPage.serviceHint")}
        </Typography>
      ) : null}
      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          {loading ? <DashboardKpiSkeleton /> : (
            <Card sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                  <Avatar sx={{ bgcolor: SERIES[0], width: 44, height: 44 }}>
                    <SupportAgentIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {t("analytics.totalRequests")}
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "total_solicitacoes",
                          metricName: "Total de Solicitações",
                          metricLabel: "Volume de solicitações",
                          metricDefinition:
                            "Quantidade total de solicitações recebidas no período (impacta dimensionamento, SLA e capacidade do time).",
                          metricValue: totalAtendimentos,
                          metricHuman: BR(totalAtendimentos),
                        }}
                      />
                    </Stack>
                    <Typography variant="h4" sx={{ color: SERIES[0], lineHeight: 1.1 }}>
                      {BR(totalAtendimentos)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("analytics.selectedPeriod")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          {loading ? <DashboardKpiSkeleton /> : (
            <Card sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                  <Avatar sx={{ bgcolor: SERIES[4] ?? SERIES[3], width: 44, height: 44 }}>
                    <GroupsIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {t("analytics.peopleContacted")}
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "clientes_contato_humano",
                          metricName: t("analytics.peopleContacted"),
                          metricLabel: t("analytics.peopleContacted"),
                          metricDefinition: t("analytics.peopleContactedAiDefinition"),
                          metricValue: clientesComContatoHumano,
                          metricHuman: BR(clientesComContatoHumano),
                        }}
                      />
                    </Stack>
                    <Typography variant="h4" sx={{ color: SERIES[4] ?? SERIES[3], lineHeight: 1.1 }}>
                      {BR(clientesComContatoHumano)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {peopleContactedMode === "leads_distinct"
                        ? t("analytics.peopleContactedHintApprox", {
                            defaultValue:
                              "Clientes distintos com solicitação no período (aproximado em volumes altos)",
                          })
                        : t("analytics.peopleContactedHint")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          {loading ? <DashboardKpiSkeleton /> : (
            <Card sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                  <Avatar sx={{ bgcolor: SERIES[1], width: 44, height: 44 }}>
                    <AccessTimeIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {t("analytics.ahtAvg")}
                      </Typography>
                      {tmaQuality?.available !== false && (
                      <Tooltip title="Explicar com IA">
                        <IconButton
                          size="small"
                          onClick={() =>
                            openAi({
                              metric: "tma",
                              metricName: "TMA (Tempo médio de atendimento)",
                              metricLabel: "Tempo médio de atendimento",
                              metricDefinition:
                                tmaQuality?.mode === "stored_estimate"
                                  ? "Estimativa do tempo médio entre abertura e finalização, com base nas solicitações que já tinham TMA gravado. Período grande demais para recalcular tudo nas mensagens."
                                  : "Tempo médio entre a abertura da solicitação e sua finalização (concluída ou perdida).",
                              metricSec: avgTMAsec,
                              metricHuman: avgTMAsec == null ? "—" : secToHuman(avgTMAsec),
                              metricValue: avgTMAsec,
                            })
                          }
                          aria-label="Explicar TMA com IA"
                        >
                          <AutoAwesomeIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="h4" sx={{ color: SERIES[1], lineHeight: 1.1 }}>{tmaFmt}</Typography>
                    <TimeMetricCaption
                      quality={tmaQuality}
                      fullHint={t("analytics.ahtHint")}
                      t={t}
                      onNarrowPeriod={readOnly ? undefined : onNarrowPeriod}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          {loading ? <DashboardKpiSkeleton /> : (
            <Card sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                  <Avatar sx={{ bgcolor: SERIES[2], width: 44, height: 44 }}>
                    <TimerIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {t("analytics.awtAvg")}
                      </Typography>
                      {tmeQuality?.available !== false && (
                      <Tooltip title="Explicar com IA">
                        <IconButton
                          size="small"
                          onClick={() =>
                            openAi({
                              metric: "tme",
                              metricName: "TME (Tempo médio de espera)",
                              metricLabel: "Tempo médio de espera",
                              metricDefinition:
                                tmeQuality?.mode === "stored_estimate"
                                  ? "Estimativa do tempo médio de espera, com base nas solicitações que já tinham TME gravado. Período grande demais para recalcular tudo nas mensagens."
                                  : "Tempo médio entre a primeira mensagem do cliente e o primeiro atendimento humano (ou tempo_espera quando disponível).",
                              metricSec: avgTMEsec,
                              metricHuman: avgTMEsec == null ? "—" : secToHuman(avgTMEsec),
                              metricValue: avgTMEsec,
                            })
                          }
                          aria-label="Explicar TME com IA"
                        >
                          <AutoAwesomeIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="h4" sx={{ color: SERIES[2], lineHeight: 1.1 }}>{tmeFmt}</Typography>
                    <TimeMetricCaption
                      quality={tmeQuality}
                      fullHint={t("analytics.awtHint")}
                      t={t}
                      onNarrowPeriod={readOnly ? undefined : onNarrowPeriod}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
        {/* <Grid item xs={12} sm={6} md={3}>
          {loading ? <Skeleton height={118} /> : (
            <KpiCard
              icon={<PsychologyIcon />}
              title="Deflexão do Bot"
              value={`${deflectionPct}%`}
              color={SERIES[3]}
              sub="Retidas no atendimento automático + Atendidas / total"
            />
          )}
        </Grid> */}
      </Grid>

      {/* Tendência + Pizzas */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ overflow: "hidden" }}>
            <CardContent sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                  {t("analytics.trendTitle")}
                </Typography>
                {!loading && (
                  <AiBtn
                    cfg={{
                      metric: "serie_temporal",
                      metricName: "Tendência de Solicitações (dia)",
                      metricDefinition: "Série diária de volume de solicitações para entender picos, sazonalidade e capacidade.",
                      metricValue: serieTemporal,
                      metricHuman: "",
                    }}
                  />
                )}
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={serieTemporal}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" tickFormatter={formatChartDayBr} />
                    <YAxis allowDecimals={false} />
                    <RTooltip labelFormatter={formatChartDayBr} />
                    <Legend />
                    <Line type="monotone" dataKey="value" name={t("analytics.seriesRequests")} stroke={SERIES[0]} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ overflow: "hidden" }}>
            <CardContent sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                  {t("analytics.inProgress")}
                </Typography>
                {!loading && (
                  <AiBtn
                    cfg={{
                      metric: "em_processamento",
                      metricName: "Em Processamento",
                      metricDefinition: "Distribuição das solicitações ainda em andamento (ex.: em atendimento, contato futuro).",
                      metricValue: emProcessamento,
                      metricHuman: "",
                    }}
                  />
                )}
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={emProcessamento} dataKey="value" nameKey="name" outerRadius={95} label>
                      {emProcessamento.map((d, i) => (
                        <Cell key={i} fill={statusCellColor(d, i)} />
                      ))}
                    </Pie>
                    <RTooltip content={<CustomPieTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ overflow: "hidden" }}>
            <CardContent sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                  {t("analytics.completed")}
                </Typography>
                {!loading && (
                  <AiBtn
                    cfg={{
                      metric: "finalizados",
                      metricName: "Finalizados",
                      metricDefinition: "Distribuição das solicitações encerradas (concluídas vs perdidas), útil para avaliar efetividade.",
                      metricValue: finalizados,
                      metricHuman: "",
                    }}
                  />
                )}
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={finalizados} dataKey="value" nameKey="name" outerRadius={95} label>
                      {finalizados.map((d, i) => (
                        <Cell key={i} fill={statusCellColor(d, i)} />
                      ))}
                    </Pie>
                    <RTooltip content={<CustomPieTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <DashboardChannelCompareSection
        loading={loading}
        channelCompare={channelCompare}
        handlerCompare={handlerCompare}
      />

      {isPreview ? null : (
      <>
      {/* Motivos de Contato – Sunburst */}
      <Grid container spacing={3} sx={{ mb: 2, alignItems: "flex-start" }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ overflow: "hidden" }}>
            <CardContent sx={{ pb: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                  {t("analytics.contactReasons")}
                </Typography>
                {!loading && motivosData.length > 0 && (
                  <Tooltip title={t("analytics.sunburstFullscreen")}>
                    <IconButton
                      size="small"
                      onClick={openSunburstFullscreen}
                      aria-label={t("analytics.sunburstFullscreen")}
                    >
                      <FullscreenIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {!loading && (
                  <AiBtn
                    cfg={{
                      metric: "motivos_sunburst",
                      metricName: "Motivos de Contato",
                      metricDefinition: "Hierarquia de motivos de contato para identificar causas raiz e oportunidades de automação/prevenção.",
                      metricValue: motivosData,
                      metricHuman: "",
                    }}
                  />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("analytics.contactReasonsDrillHint")}
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={ATENDIMENTO_SUNBURST_INLINE_HEIGHT} />
              ) : (
                <HighchartsReact highcharts={Highcharts} options={sunburstOptions} />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top caminhos (lado direito) — mesma altura útil do sunburst + scroll */}
        <Grid item xs={12} md={4}>
          <Card sx={{ width: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: "1 1 auto",
                minHeight: 0,
                overflow: "hidden",
                pb: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexShrink: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                  {t("analytics.topPaths")}
                </Typography>
                {!loading && (
                  <AiBtn
                    cfg={{
                      metric: "motivos_top_paths",
                      metricName: "Top caminhos",
                      metricDefinition: "Principais combinações de motivo (tipo/subtipos) para priorizar melhorias e respostas padrão.",
                      metricValue: motivosTop,
                      metricHuman: "",
                    }}
                  />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flexShrink: 0 }}>
                {t("analytics.topCombos")}
              </Typography>

              <Box
                sx={{
                  maxHeight: ATENDIMENTO_SUNBURST_INLINE_HEIGHT,
                  overflowY: "auto",
                  overflowX: "hidden",
                  pr: 0.5,
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {loading ? (
                  <Skeleton variant="rectangular" height={ATENDIMENTO_SUNBURST_INLINE_HEIGHT} />
                ) : (
                  <Box sx={{ display: "grid", gap: 1.25, pb: 0.25 }}>
                    {motivosTop.map((r, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) auto",
                          alignItems: "start",
                          gap: 1,
                          columnGap: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            title={r.path}
                            sx={{
                              fontWeight: 500,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              lineHeight: 1.35,
                            }}
                          >
                            {r.path}
                          </Typography>
                          <Box sx={{ mt: 0.5, height: 6, bgcolor: "grey.100", borderRadius: 999 }}>
                            <Box
                              sx={{
                                height: "100%",
                                width: `${(r.value / Math.max(1, motivosTop[0]?.value || 1)) * 100}%`,
                                bgcolor: "primary.light",
                                borderRadius: 999,
                              }}
                            />
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap", pt: 0.125 }}>
                          {BR(r.value)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela Operadores */}
      <Card>
        <CardContent>
          <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>{t("analytics.byOperator")}</Typography>
            {!loading && (
              <AiBtn
                cfg={{
                  metric: "operadores_table",
                  metricName: "Desempenho por Operador",
                  metricDefinition: "Tabela por operador com TMA/TME e volume, útil para coaching, dimensionamento e distribuição.",
                  metricValue: { sample: (rowsOperadores || []).slice(0, 25) },
                  metricHuman: "",
                }}
              />
            )}
          </Stack>

          {loading ? (
            <Skeleton variant="rectangular" height={420} />
          ) : (
            <DataGrid
              autoHeight
              rows={rowsOperadores}
              columns={columnsOperadores}
              getRowId={(r) => r?.id ?? `${r?.operador ?? "row"}-${Math.random()}`}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 10 } },
                sorting: { sortModel: [{ field: "total", sort: "desc" }] },
              }}
              sx={{
                "& .MuiDataGrid-columnHeaders": { bgcolor: "#F5F7FA" },
                "& .MuiDataGrid-cell": { alignItems: "center" },
              }}
            />
          )}

        </CardContent>
      </Card>
      </>
      )}

      <Dialog
        fullScreen
        open={sunburstFsOpen}
        onClose={() => setSunburstFsOpen(false)}
        aria-labelledby="sunburst-fullscreen-title"
        slotProps={{
          paper: {
            sx: {
              bgcolor: "background.default",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              maxHeight: "100vh",
            },
          },
        }}
      >
        <AppBar position="sticky" color="default" elevation={1} component="div" sx={{ flexShrink: 0 }}>
          <Toolbar variant="dense">
            <Typography
              id="sunburst-fullscreen-title"
              variant="subtitle1"
              component="h2"
              sx={{ flex: 1, fontWeight: 600 }}
            >
              {t("analytics.contactReasons")}
            </Typography>
            <Tooltip title={t("analytics.sunburstFullscreenClose")}>
              <IconButton
                edge="end"
                onClick={() => setSunburstFsOpen(false)}
                aria-label={t("analytics.sunburstFullscreenClose")}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            boxSizing: "border-box",
            bgcolor: "background.paper",
            overflow: "hidden",
          }}
        >
          <Box
            ref={sunburstFsBoxRef}
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "auto",
              p: { xs: 0.5, sm: 1 },
            }}
          >
            {motivosData.length > 0 && sunburstFullscreenChartSide > 0 ? (
              <Box
                sx={{
                  width: sunburstFullscreenChartSide,
                  height: sunburstFullscreenChartSide,
                  flexShrink: 0,
                }}
              >
                <HighchartsReact
                  highcharts={Highcharts}
                  options={sunburstOptionsFullscreen}
                  containerProps={{
                    style: {
                      width: sunburstFullscreenChartSide,
                      height: sunburstFullscreenChartSide,
                    },
                  }}
                  callback={(chart) => {
                    sunburstFsChartRef.current = chart;
                  }}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      </Dialog>

      {!readOnly ? (
      <MetricAiInsightsDialog
        open={metricDialogOpen}
        metric={metricDialogMetric}
        metricName={metricDialogName}
        metricLabel={metricDialogLabel}
        metricHuman={metricDialogHuman}
        period={{ start, end }}
        loading={metricInsightsMutation.isLoading}
        error={metricDialogError}
        data={metricDialogData}
        scope="atendimento"
        basePayload={metricDialogBasePayload}
        onClose={() => setMetricDialogOpen(false)}
      />
      ) : null}
    </Box>
  );
};

export default Atendimento;
